import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { ArrowLeft, FileText, BookOpen, Code, Settings, Zap } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { Card } from '../components/ui/Card';

// Documentation structure for navigation
const docSections = [
  {
    title: 'Getting Started',
    icon: BookOpen,
    items: [
      { id: 'README', title: 'Documentation Index', path: '/docs' },
    ]
  },
  {
    title: 'Features',
    icon: FileText,
    items: [
      { id: 'features/lead-notes', title: 'Lead Notes', path: '/docs/features/lead-notes' },
      { id: 'features/timeline-records', title: 'Timeline Records', path: '/docs/features/timeline-records' },
    ]
  },
  {
    title: 'API Documentation',
    icon: Code,
    items: [
      { id: 'api', title: 'API Overview', path: '/docs/api' },
      { id: 'api-helpers', title: 'API Helpers', path: '/docs/api-helpers' },
      { id: 'api/fourdeemr-webhooks-v1-inbound', title: 'FourDeeMR Webhooks: Inbound v1', path: '/docs/api/fourdeemr-webhooks-v1-inbound' },
      { id: 'api/higherlevel-proxy-v1-outbound', title: 'HigherLevel Proxy: Outbound v1', path: '/docs/api/higherlevel-proxy-v1-outbound' },
      { id: 'api/outbound-calls', title: 'Outbound Calls', path: '/docs/api/outbound-calls' },
      { id: 'api/supabase-edge-v1-inbound', title: 'Supabase Edge: Inbound v1', path: '/docs/api/supabase-edge-v1-inbound' },
      { id: 'api/zonedm-leads-v1-inbound', title: 'ZoneDM Leads: Inbound v1', path: '/docs/api/zonedm-leads-v1-inbound' },
    ]
  },
  {
    title: 'Events',
    icon: Zap,
    items: [
      { id: 'events/scheduled-jobs', title: 'Scheduled Jobs', path: '/docs/events/scheduled-jobs' },
      { id: 'events/webhooks', title: 'Webhooks', path: '/docs/events/webhooks' },
    ]
  },
  {
    title: 'Dashboard',
    icon: Settings,
    items: [
      { id: 'dashboard/services', title: 'Services', path: '/docs/dashboard/services' },
    ]
  },
  {
    title: 'Middleware',
    icon: Settings,
    items: [
      { id: 'middleware/overview', title: 'Overview', path: '/docs/middleware/overview' },
    ]
  },
  {
    title: 'Integration Guides',
    icon: FileText,
    items: [
      { id: 'integrations', title: 'Integrations Overview', path: '/docs/integrations' },
      { id: '4d-webhook', title: '4D Webhook', path: '/docs/4d-webhook' },
      { id: 'zonedm-overview', title: 'ZoneDM + 4D: Integration Overview', path: '/docs/zonedm-overview' },
      { id: 'zonedm-data-flow', title: 'ZoneDM + 4D: Data Flow Diagram', path: '/docs/zonedm-data-flow' },
      { id: 'zonedm-tasks', title: 'ZoneDM + 4D: Integration Tasks', path: '/docs/zonedm-tasks' },
    ]
  },

  {
    title: 'Extracted Code References',
    icon: Code,
    items: [
      { id: 'extracted/4dleads-webhook', title: '4dleads-webhook.js (extracted)', path: '/docs/extracted/4dleads-webhook' },
      { id: 'extracted/4dpatients', title: '4dpatients.js (extracted)', path: '/docs/extracted/4dpatients' },
      { id: 'extracted/higherlevel-proxy', title: 'higherlevel-proxy.js (extracted)', path: '/docs/extracted/higherlevel-proxy' },
      { id: 'extracted/highlevel-leads-webhook', title: 'highlevel-leads-webhook.js (extracted)', path: '/docs/extracted/highlevel-leads-webhook' },
      { id: 'extracted/oauth-callback', title: 'oauth-callback.js (extracted)', path: '/docs/extracted/oauth-callback' },
      { id: 'extracted/process-highlevel-leads', title: 'process-highlevel-leads.js (extracted)', path: '/docs/extracted/process-highlevel-leads' },
      { id: 'extracted/process-webhooks', title: 'process-webhooks.js (extracted)', path: '/docs/extracted/process-webhooks' },
      { id: 'extracted/webhook-new', title: 'webhook-new.js (extracted)', path: '/docs/extracted/webhook-new' },
      { id: 'extracted/webhook', title: 'webhook.js (extracted)', path: '/docs/extracted/webhook' },
    ]
  },
];

// Function to get the markdown file path based on docId
const getMarkdownPath = (docId: string): string => {
  const pathMap: Record<string, string> = {
    'README': '/docs/README.md',
    'features/lead-notes': '/docs/notes-feature.md',
    'features/timeline-records': '/docs/timeline-feature.md',
    'api': '/docs/api.md',
    'api-helpers': '/docs/api_helpers.md',
    'integrations': '/docs/integrations.md',
    '4d-webhook': '/docs/4d-webhook.md',
      'zonedm-overview': '/docs/zonedm-4d-integration-overview.md',
  'zonedm-data-flow': '/docs/zonedm-4d-data-flow-diagram.md',
  'zonedm-tasks': '/docs/zonedm-4d-integration-tasks.md',
    'api/fourdeemr-webhooks-v1-inbound': '/docs/api/fourdeemr-webhooks-v1-inbound.md',
    'api/higherlevel-proxy-v1-outbound': '/docs/api/higherlevel-proxy-v1-outbound.md',
    'api/outbound-calls': '/docs/api/outbound-calls.md',
    'api/supabase-edge-v1-inbound': '/docs/api/supabase-edge-v1-inbound.md',
    'api/zonedm-leads-v1-inbound': '/docs/api/zonedm-leads-v1-inbound.md',
    'events/scheduled-jobs': '/docs/events/scheduled_jobs.md',
    'events/webhooks': '/docs/events/webhooks.md',
    'dashboard/services': '/docs/dashboard/services.md',
    'middleware/overview': '/docs/middleware/overview.md',
    'extracted/4dleads-webhook': '/docs/_extracted/netlify/functions/4dleads-webhook.js.extract.md',
    'extracted/4dpatients': '/docs/_extracted/netlify/functions/4dpatients.js.extract.md',
    'extracted/higherlevel-proxy': '/docs/_extracted/netlify/functions/higherlevel-proxy.js.extract.md',
    'extracted/highlevel-leads-webhook': '/docs/_extracted/netlify/functions/highlevel-leads-webhook.js.extract.md',
    'extracted/oauth-callback': '/docs/_extracted/netlify/functions/oauth-callback.js.extract.md',
    'extracted/process-highlevel-leads': '/docs/_extracted/netlify/functions/process-highlevel-leads.js.extract.md',
    'extracted/process-webhooks': '/docs/_extracted/netlify/functions/process-webhooks.js.extract.md',
    'extracted/webhook-new': '/docs/_extracted/netlify/functions/webhook-new.js.extract.md',
    'extracted/webhook': '/docs/_extracted/netlify/functions/webhook.js.extract.md',
  };
  
  return pathMap[docId] || '/docs/README.md';
};

export default function DocsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // Extract docId from the current path
  const getDocIdFromPath = () => {
    const path = location.pathname;
    if (path === '/docs' || path === '/docs/') {
      return 'README';
    }
    // Remove '/docs/' prefix and return the rest as docId
    return path.replace('/docs/', '');
  };

  useEffect(() => {
    const loadDoc = async () => {
      setLoading(true);
      setError('');
      
      try {
        const targetDocId = getDocIdFromPath();
        const markdownPath = getMarkdownPath(targetDocId);
        
        // Fetch the markdown file
        const response = await fetch(markdownPath);
        if (response.ok) {
          const text = await response.text();
          setContent(text);
        } else {
          setError(`Documentation not found: ${targetDocId}`);
        }
      } catch (err) {
        setError('Failed to load documentation');
        console.error('Error loading doc:', err);
      } finally {
        setLoading(false);
      }
    };

    loadDoc();
  }, [location.pathname]);

  const currentDocId = getDocIdFromPath();
  const currentDoc = docSections
    .flatMap(section => section.items)
    .find(item => item.id === currentDocId);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Loading documentation...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/docs')}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Back to Documentation
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar Navigation */}
          <div className="w-80 flex-shrink-0">
            <Card className="p-6 sticky top-8 max-h-[calc(100vh-2rem)] overflow-y-auto">
              <div className="mb-6">
                <button
                  onClick={() => navigate('/docs')}
                  className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Documentation
                </button>
                <h2 className="text-lg font-semibold text-gray-900">Documentation</h2>
              </div>
              
              <nav className="space-y-6">
                {docSections.map((section) => (
                  <div key={section.title}>
                    <div className="flex items-center text-sm font-medium text-gray-900 mb-3">
                      <section.icon className="w-4 h-4 mr-2" />
                      {section.title}
                    </div>
                    <ul className="space-y-2">
                      {section.items.map((item) => (
                        <li key={item.id}>
                          <Link
                            to={item.path}
                            className={`block px-3 py-2 text-sm rounded-md transition-colors ${
                              item.id === currentDocId
                                ? 'bg-blue-100 text-blue-700 font-medium'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                            }`}
                          >
                            {item.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </nav>
            </Card>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <Card className="p-8">
              <div className="prose prose-lg max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeHighlight]}
                  components={{
                    // Custom styling for better presentation
                    h1: ({ children }) => (
                      <h1 className="text-3xl font-bold text-gray-900 mb-6 border-b border-gray-200 pb-4">
                        {children}
                      </h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
                        {children}
                      </h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                        {children}
                      </h3>
                    ),
                    code: ({ children, className }) => {
                      const isInline = !className;
                      return isInline ? (
                        <code className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-sm">
                          {children}
                        </code>
                      ) : (
                        <code className={className}>{children}</code>
                      );
                    },
                    pre: ({ children }) => (
                      <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                        {children}
                      </pre>
                    ),
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-700">
                        {children}
                      </blockquote>
                    ),
                    table: ({ children }) => (
                      <div className="overflow-x-auto">
                        <table className="min-w-full border-collapse border border-gray-300">
                          {children}
                        </table>
                      </div>
                    ),
                    th: ({ children }) => (
                      <th className="border border-gray-300 bg-gray-100 px-4 py-2 text-left font-semibold">
                        {children}
                      </th>
                    ),
                    td: ({ children }) => (
                      <td className="border border-gray-300 px-4 py-2">
                        {children}
                      </td>
                    ),
                  }}
                >
                  {content}
                </ReactMarkdown>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}